import { SyncOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Modal,
  PageHeader,
  Result,
  Skeleton,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { get, isEmpty } from 'lodash';
import React, { useState } from 'react';
import moment from 'moment';
import { useMutation, useQuery } from 'react-apollo';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import { ADMIN_USER_INVESTMENTS, INVESTOR } from './apollo_queries';
import { ADMIN_CHECK_KYC, ADMIN_SET_ACCREDITATION } from './apollo_mutations';

// Reconnected to constlending-api-next `adminUser` (see INVESTOR in apollo_queries.js). Four blocks
// (TL, 2026-09-09): Person · Accounts & KYC (individual first) · Accreditation (North Capital) ·
// Investments (`adminUserInvestments`). The last two only exist for an investor with at least one
// investment — pending or completed — so someone who never invested sees just Person and
// Accounts & KYC, and `adminUserInvestments` is never requested (TL, 2026-09-10). `kycReasons`
// replaces the legacy `kycFails` accordion; `ssn`/`dob` are gone. Per-investment movements stay on
// the note's own page.
//
// No Refresh (TL, 2026-09-12): the two things an operator does here — Check KYC and grant/revoke the
// accreditation — refetch the user themselves, so the card is never stale behind the operator's back.
// The way out is the PageHeader's own arrow, top left, discreet: `onBack`, no red button.

const KYC_COLOR = { passed: 'green', documents_required: 'orange', failed: 'red' };
const ACCREDITATION_COLOR = { VERIFIED: 'green', NOT_STARTED: 'default' };
const LOAN_STATUS_COLOR = { active: 'green', paid: 'blue', default: 'red' };

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const fmtMoney = (n) => (n == null ? '—' : money.format(n));
const fmtDate = (d) => (d ? moment(d).format('LLL') : '—');
const fmtDay = (d) => (d ? moment(d).format('ll') : '—');
const fmtPercent = (n) => (n == null ? '—' : `${n}%`);

// Individual first, entities after (the backend list is not ordered).
const sortAccounts = (accounts) =>
  [...accounts].sort(
    (a, b) => (a.type === 'individual' ? 0 : 1) - (b.type === 'individual' ? 0 : 1)
  );

function KycTag({ kyc }) {
  return <Tag color={KYC_COLOR[kyc] || 'default'}>{(kyc || 'none').toUpperCase()}</Tag>;
}

// A re-check is a DELIBERATE action, not another field of the card (TL, 2026-09-12): it asks Sila
// again for THIS account's identity verification and saves whatever Sila answers, so it can move the
// account's KYC and its reasons. Hence the confirm modal that says so in plain words before firing,
// the icon + the header's `extra` slot to keep it away from the data, and a result that names the new
// status and whether it moved. Backend: user-account/modules/kyc/services/verification.service.ts.
function CheckKycButton({ account, userId, refetch }) {
  const [check, { loading }] = useMutation(ADMIN_CHECK_KYC);
  const accountLabel =
    account.accountName ||
    (account.type === 'individual' ? 'Individual account' : 'Entity account');
  // Read BEFORE the mutation: `refetch` replaces the prop with the new status.
  const previousKyc = account.kyc || 'none';

  const reCheck = async () => {
    try {
      const { data } = await check({
        variables: { userId, userAccountId: account.id },
      });
      const currentKyc = get(data, 'adminCheckKyc.kyc') || 'none';
      await refetch();
      message.success(
        currentKyc === previousKyc
          ? `KYC unchanged: ${currentKyc}`
          : `KYC re-checked: ${currentKyc} (was ${previousKyc})`
      );
    } catch (error) {
      message.error(error.message);
    }
  };

  const confirmReCheck = () =>
    Modal.confirm({
      title: 'Re-check identity verification with Sila',
      icon: <SyncOutlined />,
      okText: 'Re-check now',
      cancelText: 'Cancel',
      content: (
        <>
          <p style={{ marginBottom: 8 }}>
            We will ask Sila again for the identity verification of{' '}
            <strong>{accountLabel}</strong> ({account.type}) and save the answer.
          </p>
          <p style={{ marginBottom: 0 }}>
            This account&apos;s KYC status may change — for example from in review to approved or
            rejected — along with its reasons. No money is moved and the investor&apos;s other
            accounts are not touched.
          </p>
        </>
      ),
      onOk: reCheck,
    });

  return (
    <Button
      size="small"
      icon={<SyncOutlined />}
      loading={loading}
      disabled={loading}
      onClick={confirmReCheck}
    >
      Check KYC
    </Button>
  );
}

function AccountCard({ account, userId, refetch }) {
  const isIndividual = account.type === 'individual';
  const holder = `${account.firstName || ''} ${account.lastName || ''}`.trim();
  return (
    <Card
      type="inner"
      size="small"
      title={
        <Space>
          {account.accountName || (isIndividual ? 'Individual account' : 'Entity account')}
          <Tag>{account.type}</Tag>
          {account.isDefault && <Tag color="blue">default</Tag>}
        </Space>
      }
      extra={<CheckKycButton account={account} userId={userId} refetch={refetch} />}
    >
      <Descriptions column={2} size="small" bordered>
        <Descriptions.Item label="KYC">
          <KycTag kyc={account.kyc} />
        </Descriptions.Item>
        <Descriptions.Item label="KYC synced">{fmtDate(account.kycSyncedAt)}</Descriptions.Item>
        <Descriptions.Item label="Holder">{holder || '—'}</Descriptions.Item>
        <Descriptions.Item label="Email">{account.email || '—'}</Descriptions.Item>
        {isIndividual ? (
          <Descriptions.Item label="Certified">{account.isCertified ? 'Yes' : 'No'}</Descriptions.Item>
        ) : (
          <Descriptions.Item label="EIN">{account.ein || '—'}</Descriptions.Item>
        )}
        <Descriptions.Item label="Investment account type">
          {account.investmentAccountType || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Verification UUID">
          {account.verificationUuid || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Created">{fmtDate(account.createdAt)}</Descriptions.Item>
      </Descriptions>
      {!isEmpty(account.kycReasons) && (
        <Alert
          style={{ marginTop: 12 }}
          type="warning"
          showIcon
          message="KYC reasons"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {account.kycReasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          }
        />
      )}
    </Card>
  );
}

// Grant or revoke, same op: `adminSetAccreditation({ userId, accredited, reason })`. The reason is
// mandatory and audited by the backend (>= 10 chars after trim), for revoking too.
function AccreditationModal({ mode, userId, onClose, onDone }) {
  const [form] = Form.useForm();
  const [setAccreditation, { loading }] = useMutation(ADMIN_SET_ACCREDITATION);
  const grant = mode === 'grant';

  const submit = async ({ reason }) => {
    try {
      await setAccreditation({
        variables: { data: { userId, accredited: grant, reason: reason.trim() } },
      });
      await onDone();
      message.success(grant ? 'Investor marked as accredited' : 'Accreditation revoked');
      form.resetFields();
      onClose();
    } catch (error) {
      message.error(
        error.message.includes('USER_ACCOUNT_NOT_FOUND')
          ? 'This user has no individual account: it cannot be accredited.'
          : error.message.replace('GraphQL error: ', '')
      );
    }
  };

  return (
    <Modal
      visible={Boolean(mode)}
      title={grant ? 'Mark as accredited' : 'Revoke accreditation'}
      okText={grant ? 'Mark as accredited' : 'Revoke'}
      okButtonProps={{ danger: !grant, loading }}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnClose
    >
      <Alert
        style={{ marginBottom: 16 }}
        type={grant ? 'info' : 'warning'}
        showIcon
        message={
          grant
            ? 'Writes the accreditation on the individual account with your admin id, the date and the reason. If this investor has a signed Credit Fund commitment waiting on accreditation, the held funds are released now.'
            : 'The investor goes back to NOT_STARTED. The decision is appended to the audit log with your admin id and the reason; nothing is deleted and no money moves.'
        }
      />
      <Form form={form} layout="vertical" onFinish={submit} preserve={false}>
        <Form.Item
          name="reason"
          label="Reason (audited)"
          rules={[
            { required: true, message: 'A reason is required' },
            {
              validator: (_, value) =>
                (value || '').trim().length >= 10
                  ? Promise.resolve()
                  : Promise.reject(new Error('At least 10 characters')),
            },
          ]}
        >
          <Input.TextArea
            rows={3}
            autoFocus
            placeholder={
              grant
                ? 'e.g. Accountant letter verified, expires 2027-03'
                : 'e.g. Letter expired, investor did not renew'
            }
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function AccreditationCard({ user, individual, refetch }) {
  const [mode, setMode] = useState(null);
  const status = get(user, 'accreditation.status', '');
  const verified = status === 'VERIFIED';
  const verificationUrl = get(user, 'accreditation.verificationUrl');
  return (
    <Card
      title="Accreditation (North Capital)"
      size="small"
      extra={
        verified ? (
          <Button danger size="small" onClick={() => setMode('revoke')}>
            Revoke accreditation
          </Button>
        ) : (
          <Button
            type="primary"
            size="small"
            disabled={!individual}
            title={individual ? '' : 'No individual account: cannot be accredited'}
            onClick={() => setMode('grant')}
          >
            Mark as accredited
          </Button>
        )
      }
    >
      <Descriptions column={2} size="small" bordered>
        <Descriptions.Item label="Status">
          <Tag color={ACCREDITATION_COLOR[status] || 'default'}>{status || '—'}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Verified at">
          {fmtDate(get(user, 'accreditation.verifiedAt'))}
        </Descriptions.Item>
        <Descriptions.Item label="Verification URL">
          {verificationUrl ? (
            <a href={verificationUrl} target="_blank" rel="noopener noreferrer">
              {verificationUrl}
            </a>
          ) : (
            '—'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Individual account flag">
          {individual
            ? individual.accreditedInvestorStatus
              ? `Accredited · ${fmtDate(individual.accreditedAt)}`
              : 'Not accredited'
            : 'No individual account'}
        </Descriptions.Item>
      </Descriptions>
      <Alert
        style={{ marginTop: 12 }}
        type="info"
        showIcon
        message="Accreditation belongs to the person and is derived from the INDIVIDUAL account. Entity accounts do not inherit it."
      />
      {mode && (
        <AccreditationModal
          mode={mode}
          userId={user.id}
          onClose={() => setMode(null)}
          onDone={refetch}
        />
      )}
    </Card>
  );
}

const INVESTMENT_COLUMNS = [
  {
    title: 'Note',
    dataIndex: 'name',
    render: (name, row) => (
      <Space direction="vertical" size={0}>
        <Link to={`/properties-and-investments/${row.investmentId}`}>
          {name || <span style={{ color: '#c0392b' }}>(note not found)</span>}
        </Link>
        <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
          {[row.noteType, row.noteListing].filter(Boolean).join(' · ')}
        </span>
      </Space>
    ),
  },
  { title: 'Net yield', dataIndex: 'netYield', render: fmtPercent, align: 'right' },
  { title: 'Maturity', dataIndex: 'maturityDate', render: fmtDay },
  {
    title: 'Loan status',
    dataIndex: 'loanStatus',
    render: (s) => (s ? <Tag color={LOAN_STATUS_COLOR[s] || 'default'}>{s}</Tag> : '—'),
  },
  { title: 'Invested', dataIndex: 'investedAmount', render: fmtMoney, align: 'right' },
  { title: 'Repaid', dataIndex: 'repaidAmount', render: fmtMoney, align: 'right' },
  { title: 'Balance', dataIndex: 'balance', render: fmtMoney, align: 'right' },
  { title: 'Interest earned', dataIndex: 'interestEarned', render: fmtMoney, align: 'right' },
];

const sum = (rows, field) => rows.reduce((acc, row) => acc + (row[field] || 0), 0);

function InvestmentsCard({ userId }) {
  const { data, loading, error } = useQuery(ADMIN_USER_INVESTMENTS, { variables: { userId } });
  const rows = get(data, 'adminUserInvestments', []);
  return (
    <Card title="Investments" size="small">
      {error ? (
        <Alert type="error" showIcon message={`Could not load the investments: ${error.message}`} />
      ) : (
        <Table
          rowKey="investmentId"
          size="small"
          columns={INVESTMENT_COLUMNS}
          dataSource={rows}
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'No investments yet' }}
          summary={() =>
            rows.length > 1 && (
              <Table.Summary.Row style={{ fontWeight: 600 }}>
                <Table.Summary.Cell index={0} colSpan={4}>
                  Total
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  {fmtMoney(sum(rows, 'investedAmount'))}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  {fmtMoney(sum(rows, 'repaidAmount'))}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right">
                  {fmtMoney(sum(rows, 'balance'))}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7} align="right">
                  {fmtMoney(sum(rows, 'interestEarned'))}
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )
          }
        />
      )}
    </Card>
  );
}

export function InvestorDetails() {
  const history = useHistory();
  const match = useRouteMatch();
  const userId = get(match, 'params.id', '');
  const goBack = () => history.push('/users');

  const { data, error, loading, refetch } = useQuery(INVESTOR, { variables: { userId } });

  if (loading) {
    return <Skeleton active paragraph={{ rows: 10 }} />;
  }

  if (error) {
    return (
      <Result
        status="error"
        title="Could not load this user"
        subTitle={error.message}
        extra={<Button onClick={goBack}>Back to users</Button>}
      />
    );
  }

  const user = get(data, 'adminUser', {});
  const accounts = sortAccounts(get(user, 'accounts', []));
  const individual = accounts.find((a) => a.type === 'individual');
  const firstName = user.firstName || (individual && individual.firstName) || '';
  const lastName = user.lastName || (individual && individual.lastName) || '';
  const fullName = `${firstName} ${lastName}`.trim();
  const fullAddress = [user.address, user.city, user.state, user.zip].filter(Boolean).join(', ');
  // No investment (pending or completed) => nothing to accredit and nothing to list: both the North
  // Capital block and the Investments card are hidden, and the investments query never mounts.
  const hasInvestment = !!(user.hasPendingInvestment || user.hasCompletedInvestment);

  return (
    <>
      <PageHeader
        onBack={goBack}
        title={fullName || user.email}
        subTitle={fullName ? user.email : null}
        style={{ padding: '0 0 16px' }}
        tags={[
          user.type ? (
            <Tag key="type" color="blue">
              {user.type}
            </Tag>
          ) : null,
          user.isAdmin ? (
            <Tag key="admin" color="purple">
              Admin
            </Tag>
          ) : null,
          <Tag key="email" color={user.isEmailVerified ? 'green' : 'default'}>
            {user.isEmailVerified ? 'Email verified' : 'Email not verified'}
          </Tag>,
        ].filter(Boolean)}
      />
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card title="Person" size="small">
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="Phone">
              {user.phoneNumber ? <a href={`tel:${user.phoneNumber}`}>{user.phoneNumber}</a> : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Address">{fullAddress || '—'}</Descriptions.Item>
            <Descriptions.Item label="Registered">{fmtDate(user.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="Last updated">{fmtDate(user.updatedAt)}</Descriptions.Item>
            <Descriptions.Item label="Alto user id">{user.altoUserId || '—'}</Descriptions.Item>
            <Descriptions.Item label="User id">
              <code>{user.id}</code>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="Accounts & KYC" size="small">
          {isEmpty(accounts) ? (
            <Alert type="warning" showIcon message="This user has no Sila accounts yet." />
          ) : (
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              {accounts.map((account) => (
                <AccountCard
                  key={account.id}
                  account={account}
                  userId={userId}
                  refetch={refetch}
                />
              ))}
            </Space>
          )}
        </Card>

        {hasInvestment && (
          <>
            <AccreditationCard user={user} individual={individual} refetch={refetch} />
            <InvestmentsCard userId={userId} />
          </>
        )}
      </Space>
    </>
  );
}
